import { Component, effect, EffectRef, ElementRef, inject, OnDestroy, OnInit, Signal, ViewChild } from '@angular/core';
import { DvmService } from '@services/dvm.service';
import { PreviewComponent } from './preview/preview.component';
import { Viewer3dEvent, Viewer3dService } from '@3ddv/ngx-dvm-internal';
import { Subscription } from 'rxjs';
import { Viewer3dRelatedPreviewObject, Viewer3dRelatedPreviewsEventObject } from '@3ddv/dvm-internal';
import anime from 'animejs';
import { RelatedPreviewsService } from '@services/components/related-previews.service';
import { AppViews } from '@models/enums/dvm/DvmViews.enum';

@Component({
  selector: 'app-related-previews',
  standalone: true,
  imports: [
    PreviewComponent
  ],
  templateUrl: './related-previews.component.html',
  styleUrl: './related-previews.component.css'
})
export class RelatedPreviewsComponent implements OnInit, OnDestroy {

  @ViewChild('previewsContainer')
  private readonly previewsContainer!: ElementRef<HTMLDivElement>;

  // SERVICES
  private readonly dvmService:             DvmService                                 = inject(DvmService);
  private readonly relatedPreviewsService: RelatedPreviewsService                     = inject(RelatedPreviewsService);

  // GLOBAL STATE                   
  private readonly viewer3d:               Viewer3dService                            = this.dvmService.viewer3d;
  private readonly view:                   Signal<string | null>                      = this.dvmService.view;
    
    
  // COMPONENT STATE     
  private showElements:                    boolean                                    = false;
  private readonly suscriptions:           Subscription[]                             = [];
  private readonly relatedPreviews:        Map<string, Viewer3dRelatedPreviewObject>  = new Map<string, Viewer3dRelatedPreviewObject>();
  protected prevPreview:                   Viewer3dRelatedPreviewObject | null        = null;

  // EFFECTS
  private onViewChange:                    EffectRef                                  = effect((): void => {
    if(this.view()) {
      this.relatedPreviewsService.resetCurrentPreview();
    }
  }, { allowSignalWrites: true });

  // GETTERS
  public get previews(): Viewer3dRelatedPreviewObject[] {
    return Array.from(this.relatedPreviews.values());
  }

  // LIFECYCLE METHODS
  ngOnInit(): void {
    
    this.initComponent();

  }

  ngOnDestroy(): void {
    this.suscriptions.forEach((subscription: Subscription): void => subscription.unsubscribe());
  }

  // METHODS
  private initComponent(): void {
    
    this.viewer3d.waitInitialize().subscribe(():void => this.initSuscriptions());

  }

  private initSuscriptions(): void {

    this.suscriptions.push(

      this.viewer3d.getObservable('update_related_preview').subscribe((event: Viewer3dEvent<Viewer3dRelatedPreviewsEventObject>): void => this.updateRelatedPreviews(event['event']['related_previews'])),

      this.viewer3d.getObservable('show_related_preview').subscribe((): void => this.togglePreviews(true)),

      this.viewer3d.getObservable('hide_related_preview').subscribe((): void => this.togglePreviews(this.view() === AppViews.Pano)),

      this.viewer3d.getObservable('reset_related_preview').subscribe((): void => this.togglePreviews(this.viewer3d.getVenueId() === this.dvmService.currentVenue)),

    )

  }

  private updateRelatedPreviews(relatedPreview: Viewer3dRelatedPreviewObject): void  {

    // TEMP FIX
    if(this.relatedPreviews.has(relatedPreview.id)) {

      const isSameView: boolean = this.relatedPreviews.get(relatedPreview.id)?.view_id === relatedPreview.view_id;

      if(!isSameView) this.prevPreview = this.relatedPreviews.get(relatedPreview.id) ?? null;
        

    }

    this.relatedPreviews.set(relatedPreview.id, relatedPreview);    
    
  }
  
  private togglePreviews(show: boolean): void {

    if(this.showElements === show) return;

    setTimeout(() => {
          const targets = Array.from(this.previewsContainer.nativeElement.children);
          
          if(targets.length === 0) return;
          
          anime({
              targets: targets,
              opacity: show ? 1 : 0,
              translateY: show ? [20, 0] : [0, 20],
              easing: 'easeInOutQuad',
              delay: anime.stagger(100),
              duration: 300,
              complete: (): void => { 
                  if(!show) {
                      this.relatedPreviews.clear();
                      this.showElements = false;
                  } else {
                      this.showElements = true;
                  }
              } 
          });
      }, 0);

  }

  protected checkPreviews(preview: Viewer3dRelatedPreviewObject): void {

    this.relatedPreviewsService.currentPreview.set(preview);

    if(this.prevPreview) {
      this.prevPreview.id = preview.id;
      this.relatedPreviews.set(preview.id, this.prevPreview);
      this.prevPreview = preview;
    }

  }

  protected handlePreviewError(preview: Viewer3dRelatedPreviewObject): void {
    
    // Removemos el preview que falló del Map
    this.relatedPreviews.delete(preview.id);
    
    // Si no quedan previews, ocultamos el contenedor
    if (this.relatedPreviews.size === 0) this.togglePreviews(false);
    
  }

}
